import {
  changePatternMaterial,
  changePatternSize,
  createRandomMaterialPattern,
  createRandomPatternFragment,
  MaterialPattern,
  parseMaterialPatternString,
  scalePatternString,
  sumPattern,
  toPatternString,
} from 'Pattern';
import {
  BoardDetails,
  BoardParams,
  BoardType,
  Dimensions2D,
  Material,
  OneArray,
  ParamsPanelSelection,
  Renderable,
  RenderableMetadata,
  Settings,
  TwoArray,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import {
  groupBy,
  mapMapEntries,
  range,
  sample,
  scaleNumber,
  sumBy,
} from 'utils/utils';

export default abstract class ZigWheelBase<
  T extends BoardType.Pinwheel | BoardType.ZigZag
> extends BoardBase<T> {
  getPattern(): MaterialPattern {
    return parseMaterialPatternString(this.params.pattern);
  }

  abstract getRenderables(): Array<Renderable<T>>;

  getIsRenderableHighlighted(
    renderable: Renderable<T>,
    selection: Renderable<T>
  ): boolean {
    return (
      renderable.metadata.fragmentIndex === selection.metadata.fragmentIndex
    );
  }

  getBoardDetails(settings: Settings): BoardDetails {
    const { kerf, planeBuffer } = settings;
    const { numVertical, numHorizontal, thickness } = this.params;
    const pattern = this.getPattern();
    const patternSize = sumPattern(pattern);
    const patternFragmentsByMaterial = groupBy(pattern, 'key');
    const numBlocks = numVertical * numHorizontal;

    // const finalFBMByMaterial = mapValues(
    //   patternFragmentsByMaterial,
    //   dimensions => ({
    //     height: numVertical * thickness,
    //     width: sumBy(dimensions, 'size'),
    //     thickness: patternSize,
    //   }),
    // );

    const startingStockLength =
      numBlocks * (thickness + planeBuffer) + (numBlocks - 1) * kerf;

    const startingStockThickness = patternSize + planeBuffer;

    const startingStockByMaterial = mapMapEntries(
      patternFragmentsByMaterial,
      (material, dimensions) => ({
        material,
        dimensions: {
          length: startingStockLength,
          thickness: startingStockThickness,
          width:
            sumBy(dimensions, ({ size }) => size + planeBuffer) +
            (dimensions.length - 1) * kerf,
        },
      })
    );

    return { idealStartingStock: [{ stock: startingStockByMaterial }] };
  }

  handleSelectedMaterialChange(
    { metadata }: Renderable<T>,
    material: Material
  ) {
    // @ts-ignore
    return this.updateParams({
      pattern: changePatternMaterial(
        this.params.pattern,
        metadata.fragmentIndex,
        material
      ),
    });
  }

  handleSelectedSizeChange({ metadata }: Renderable<T>, size: number) {
    // @ts-ignore
    return this.updateParams({
      pattern: changePatternSize(
        this.params.pattern,
        metadata.fragmentIndex,
        size
      ),
    });
  }

  // @ts-ignore
  previewSelectionToParamsPanelSelection(
    selection: RenderableMetadata<T>
  ): ParamsPanelSelection<BoardType.Chevron> {
    return {
      renderableIndex: selection.fragmentIndex,
    };
  }

  paramsPanelSelectionToPreviewSelection(
    selection: ParamsPanelSelection<BoardType.Chevron>
  ): null | RenderableMetadata<T> {
    // @ts-ignore
    return selection == null
      ? null
      : {
          fragmentIndex: selection.renderableIndex,
          renderableIndex: selection.renderableIndex,
          rowIndex: 0,
          colIndex: 0,
        };
  }

  scaleParams(params: BoardParams<T>, scaleFactor: number): BoardParams<T> {
    return {
      ...params,
      thickness: scaleNumber(params.thickness, scaleFactor),
      pattern: scalePatternString(params.pattern, scaleFactor),
    };
  }
}

export function createRandom<T extends BoardType.Pinwheel | BoardType.ZigZag>(
  materials: TwoArray<Material>,
  size: Dimensions2D
): BoardParams<T> {
  const maxPatternSize = Math.abs(size.height - size.width);
  const potentialPatternSizes: OneArray<number> = [maxPatternSize];
  while (potentialPatternSizes[0] >= 2) {
    potentialPatternSizes.unshift(potentialPatternSizes[0] / 2);
  }
  const patternSize = sample(potentialPatternSizes) ?? 2;
  // In order for the pattern to look like a pinwheel,
  // the first fragment needs to be big, at least half the size
  const startingFragmentSize =
    sample(range(patternSize / 2, patternSize - 0.5, 0.25)) ?? patternSize / 2;
  const startingFragment = createRandomPatternFragment(
    startingFragmentSize,
    startingFragmentSize,
    materials
  );

  return {
    numHorizontal: size.width / patternSize,
    numVertical: size.height / patternSize,
    pattern: toPatternString([
      startingFragment,
      ...createRandomMaterialPattern(
        1 / 4,
        1,
        patternSize - startingFragmentSize,
        materials,
        startingFragment.key
      ),
    ]),
    thickness: 1,
  } as BoardParams<T>;
}
